import React, { useState, useEffect } from "react";
import { Button, Grid, MenuItem } from "@mui/material";
import { H4 } from "components/Typography";
import { useTranslation } from "react-i18next";
import AppTextField from "components/input-fields/AppTextField";
import { useFormik } from "formik";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as Yup from "yup"; // component props interface
import XLSX from "sheetjs-style";
import api from "utils/api";
import toast from "react-hot-toast";
import { format } from "date-fns";
import moment from "moment";
import { KeyboardArrowDown } from "@mui/icons-material";

const TrainingQuizPerModule = ({ quizType }) => {
  const { t } = useTranslation();

  const [modules, setModules] = useState([]);

  useEffect(() => {
    const fetchModules = async () => {
      let result = await api.getAllTrainingModules();
      if (result.success) {
        setModules([
          { id: -1, name: t("Reports.all_modules") },
          ...result.data.map((item) => ({
            id: item.id,
            name: item.module_trans[0].name,
          })),
        ]);
      } else {
        toast.error("server error");
        return;
      }
    };
    fetchModules();
  }, []);

  const initialValues = {
    module: -1,
  };

  const validationSchema = Yup.object({
    module: Yup.number().required(t("General.required")),
  });

  const { handleSubmit, touched, errors, values, setFieldValue } = useFormik({
    initialValues,
    validationSchema: validationSchema,
  });

  // if quizType changes, we need to reset the group input and disable the export button
  useEffect(() => {
    setFieldValue("module", -1);
  }, [quizType]);

  const setCoverData = (coverSheet, moduleId) => {
    // company name
    XLSX.utils.sheet_add_aoa(
      coverSheet,
      [
        [
          `${t("Reports.report")}:`,
          t("Reports.training_quiz.per_module_title"),
        ],
      ],
      { origin: "A1" }
    );

    let currentRow = 3;
    // module name
    const moduleName =
      moduleId === -1
        ? t("Reports.all_modules")
        : modules.find((m) => m.id === moduleId)?.name;

    XLSX.utils.sheet_add_aoa(
      coverSheet,
      [[`${t("Reports.fields.module")}:`, `${moduleName}`]],
      { origin: `A${currentRow}` }
    );

    // --------------------------------------------

    // report generated
    XLSX.utils.sheet_add_aoa(
      coverSheet,
      [
        [
          `${t("Reports.report_generated")} ${format(
            new Date(),
            "eeee dd MMMM yyyy"
          )}`,
        ],
      ],
      { origin: "G1" }
    );
  };

  const exportToExcel = async () => {
    const moduleId = values.module;

    const result = await api.getTrainingQuizReportByModule(moduleId);

    const modules = result.data || [];

    if (!modules.length) {
      toast.error(t("General.no_data"));
      return;
    }

    const workbook = XLSX.utils.book_new();

    // Cover worksheet
    const coverSheet = XLSX.utils.json_to_sheet([]);
    setCoverData(coverSheet, moduleId);
    XLSX.utils.book_append_sheet(workbook, coverSheet, t("General.report"));

    // Create a new worksheet for each module
    modules.forEach((module) => {
      const worksheet = XLSX.utils.json_to_sheet([]);

      const columns = [
        // { label: t("Reports.fields.module"), key: "module" },
        { label: t("Reports.fields.employee_id"), key: "employee_id" },
        { label: t("Reports.fields.employee_name"), key: "employee_name" },
        { label: t("Reports.fields.group"), key: "group" },
        { label: t("Reports.fields.quiz_date"), key: "quiz_date" },
        { label: t("Reports.fields.difficulty"), key: "difficulty" },
        { label: t("Reports.fields.module_score"), key: "module_score" },
      ];

      const topics = [];
      // get all topics from all quizzes
      module?.quizzes.forEach((quiz) => {
        quiz.topics?.forEach((topic) => {
          if (!topics.find((t) => t.topicId === topic.topicId)) {
            topics.push(topic);
          }
        });
      });

      // add topics to the columns in the same order
      topics.forEach((topic, index) => {
        columns.push({ label: topic.topicName, key: topic.topicId });
      });

      const excelData = [];
      // add data for each instructor
      module?.quizzes.forEach((quiz) => {
        const score = (quiz.actual_score / quiz.possible_score) * 100;

        const row = [
          // quiz?.moduleName,
          quiz?.employeeId,
          quiz?.employeeName,
          quiz?.companyGroupName,
          moment(quiz?.createdAt).format("DD/MM/YYYY"),
          t(`Question.fields.difficulty.${quiz?.difficulty}`),
          Math.round(score * 100) / 100 + "%",
        ];

        topics.forEach((topic) => {
          const _topic = quiz.topics?.find((t) => t.topicId === topic.topicId);

          if (_topic) {
            const score =
              ((_topic?.actual_score || 0) / (_topic?.possible_score || 1)) *
              100;

            row.push(Math.round(score * 100) / 100 + "%");
          } else {
            row.push("-");
          }
        });

        excelData.push(row);
      });

      XLSX.utils.sheet_add_aoa(worksheet, [
        columns.map((column) => column.label),
      ]);
      XLSX.utils.sheet_add_json(worksheet, excelData, {
        origin: "A2",
        skipHeader: true,
      });

      if (module.moduleName.length > 31) {
        module.moduleName = module.moduleName.substring(0, 28) + "...";
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, module.moduleName);
    });

    // Export Excel file
    const fileName = t("Reports.training_quiz.per_module_title");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  return (
    <>
      <H4 pb={2}>{t("Reports.training_quiz.per_module_title")}</H4>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={1} alignContent="center" alignItems={"center"}>
          <Grid item>
            <AppTextField
              select
              name="module"
              onChange={(e) => setFieldValue("module", e.target.value)}
              disabled={false}
              sx={{ width: 300 }}
              label={t("Reports.fields.module")}
              SelectProps={{
                IconComponent: KeyboardArrowDown,
                value: values.module,
              }}
              error={Boolean(touched.module && errors.module)}
              helperText={touched.module && errors.module}
            >
              <div></div>
              {modules?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </AppTextField>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={exportToExcel}
            >
              {t("Reports.export")}
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default TrainingQuizPerModule;
